import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { FloatLabelModule } from 'primeng/floatlabel';
import { ListboxModule } from 'primeng/listbox';
import { SplitterModule } from 'primeng/splitter';
import { UiNotificationService } from '../../services/ui-notification.service';
import { ApiService } from '../../services/api.service';
import { AlertMeasureEvent } from '../../models/alert';
import { firstValueFrom, Subject } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { Graph } from '../../models/graph';
import { SignalComponent } from '../../components/signal/signal.component';


@Component({
  selector: 'app-breath-signals-dialog',
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    DividerModule,
    TranslatePipe,
    SplitterModule,
    FloatLabelModule,
    DatePickerModule,
    ListboxModule,
    ButtonModule,
    SignalComponent
  ],
  templateUrl: './breath-signals-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: ``,
})
export class BreathSignalsDialogComponent implements OnChanges {
  @Input() messageService!: UiNotificationService;
  @Input() userId: string = "";
  @Input() lastMeasure: number = 0;

  // visibilità dialog (bidirezionale)
  @Input() visible : boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  endDate: Date = new Date(Date.now());
  startDate: Date = new Date(Date.now() - (60 * 60 * 1000));

  loading: boolean = false;
  segnaleFreqRespiratoria: Graph[][] = [];
  segnaleRespiro: Graph[][] = [];
  events: AlertMeasureEvent[] = [];

  xMax: number = this.endDate.getTime();
  xMin: number = this.startDate.getTime();

  zoomLevel: Subject<any> = new Subject<any>();
  zoomCommand: Subject<number> = new Subject<number>();

  constructor(private apiService: ApiService) {
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['visible'] && this.visible && this.userId != "") {
      this.resetParameters();
      this.loadData();
    }
  }

  onApply() {
    this.setLimits();
    this.loadData();
  }

  onClose() {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  onZoomChanged(scales: any) {
    this.zoomLevel.next(scales);
  }

  onZoomCommand(event: number) {
    this.zoomCommand.next(event)
  }

  private async loadData() : Promise<void> {
    this.loading = true;

    try {
      const [fr, r, a] = await Promise.all([
        firstValueFrom(this.apiService.getMeasure(this.userId, this.startDate, this.endDate, 1)),
        firstValueFrom(this.apiService.getWaveform(this.userId, this.startDate, this.endDate, 7)),
        firstValueFrom(this.apiService.getAlertMeasures(this.userId, this.startDate, this.endDate))
      ]);

      this.segnaleFreqRespiratoria = fr;
      this.segnaleRespiro = r;
      this.events = a;

      this.loading = false;
    } catch (error: any) {
      this.loading = false;
      if (error instanceof HttpErrorResponse) {
        this.messageService.showError(error);
      } else {
        const err = error as Error;
        this.messageService.showErrorMessage(err.message);
      }
    }
  }

  private resetParameters() {
    this.endDate = new Date(this.lastMeasure + (5 * 60 * 1000));
    this.startDate = new Date(this.lastMeasure - (55 * 60 * 1000));

    this.setLimits();

    this.segnaleFreqRespiratoria = [[]];
    this.segnaleRespiro = [[]];
    this.events = [];
  }

  private setLimits() {
    this.xMax = this.endDate.getTime();
    this.xMin = this.startDate.getTime();
  }
}
